import { formatAppDateTime } from "./appTimeFormat";

const LIVE_STATUS_SHORT = new Set([
  "1H", "2H", "HT", "ET", "P", "LIVE", "INPLAY", "INP", "BT", "SUSP", "INT",
]);

const TERMINAL_STATUS_SHORT = new Set([
  "FT", "AET", "PEN", "FT_PEN", "CANC", "ABD", "AWD", "WO",
]);

const NOT_STARTED_STATUS_SHORT = new Set(["NS", "TBD", "PST", ""]);

const MAX_LIVE_WINDOW_SECONDS = 4 * 60 * 60;

export const FIXTURE_TAB = {
  NEED_ODDS: "needOdds",
  UPCOMING: "upcoming",
  LIVE: "live",
  PAST: "past",
};

const FIXTURE_TAB_IDS = new Set(Object.values(FIXTURE_TAB));

/** Valid `tab` query value for the fixtures desk, or null. */
export const parseFixtureDeskTab = (tabParam) => {
  const id = String(tabParam ?? "").trim();
  return FIXTURE_TAB_IDS.has(id) ? id : null;
};

export const fixturesDeskPath = (houseId, tab) => {
  const base = `/app/bethouse/manage/${String(houseId)}/fixtures`;
  const resolved = parseFixtureDeskTab(tab);
  return resolved ? `${base}?tab=${resolved}` : base;
};

export const fixtureTabLabel = (id) => {
  if (id === FIXTURE_TAB.NEED_ODDS) return "Needs odds";
  if (id === FIXTURE_TAB.UPCOMING) return "Upcoming";
  if (id === FIXTURE_TAB.LIVE) return "Live";
  return "Past";
};

/** Left-rail bucket nav — order matches operator workflow */
export const FIXTURE_BUCKET_NAV = [
  {
    id: FIXTURE_TAB.UPCOMING,
    hint: "Scheduled & accepting tickets",
    tone: "upcoming",
  },
  {
    id: FIXTURE_TAB.LIVE,
    hint: "Kickoff in play",
    tone: "live",
  },
  {
    id: FIXTURE_TAB.NEED_ODDS,
    hint: "Upcoming games still waiting for prices",
    tone: "warn",
  },
  {
    id: FIXTURE_TAB.PAST,
    hint: "Finished in the last 24 hours",
    tone: "past",
  },
];

export const getKickoffUnixSeconds = (item) => {
  const ts = Number(item?.timestamp);
  if (Number.isFinite(ts) && ts > 0) return ts;
  const dateStr = item?.date || item?.fixture?.date;
  if (dateStr) {
    const parsed = Date.parse(dateStr);
    if (!Number.isNaN(parsed)) return Math.floor(parsed / 1000);
  }
  return null;
};

export const isLiveFixture = (item) => {
  const short = String(item?.status?.short || "").toUpperCase();
  const long = String(item?.status?.long || "").toLowerCase();
  return (
    LIVE_STATUS_SHORT.has(short) ||
    long.includes("live") ||
    long.includes("in play")
  );
};

export const isTerminalFixture = (item) => {
  const short = String(item?.status?.short || "").toUpperCase();
  const long = String(item?.status?.long || "").toLowerCase();
  if (TERMINAL_STATUS_SHORT.has(short)) return true;
  return (
    long.includes("finished") ||
    long.includes("full time") ||
    long.includes("cancelled") ||
    long.includes("canceled") ||
    long.includes("abandoned")
  );
};

export const bucketFixture = (item) => {
  const now = Math.floor(Date.now() / 1000);
  const ko = getKickoffUnixSeconds(item);
  const short = String(item?.status?.short || "").toUpperCase();
  const hasKo = Number.isFinite(ko);
  const kickoffFuture = hasKo && ko > now;
  const kickoffPassed = hasKo && ko <= now;
  const secondsSinceKickoff = hasKo ? now - ko : null;
  const withinLiveWindow =
    kickoffPassed && secondsSinceKickoff <= MAX_LIVE_WINDOW_SECONDS;
  const longSinceKickoff =
    kickoffPassed && secondsSinceKickoff > MAX_LIVE_WINDOW_SECONDS;

  if (isTerminalFixture(item)) return FIXTURE_TAB.PAST;
  if (longSinceKickoff) return FIXTURE_TAB.PAST;

  if (!item?.oddsConfigured) {
    if (kickoffFuture) return FIXTURE_TAB.NEED_ODDS;
    if (withinLiveWindow) {
      if (isLiveFixture(item) || !NOT_STARTED_STATUS_SHORT.has(short)) {
        return FIXTURE_TAB.LIVE;
      }
      return FIXTURE_TAB.NEED_ODDS;
    }
    // Kickoff passed without odds — never listed; treat as off-board, not needs odds.
    return isLiveFixture(item) ? FIXTURE_TAB.LIVE : FIXTURE_TAB.PAST;
  }

  if (kickoffFuture) return FIXTURE_TAB.UPCOMING;
  if (withinLiveWindow) return FIXTURE_TAB.LIVE;
  if (isLiveFixture(item)) return FIXTURE_TAB.LIVE;
  if (NOT_STARTED_STATUS_SHORT.has(short)) return FIXTURE_TAB.UPCOMING;
  return FIXTURE_TAB.UPCOMING;
};

export const bucketFixtures = (fixtures = []) => {
  const out = {
    [FIXTURE_TAB.NEED_ODDS]: [],
    [FIXTURE_TAB.UPCOMING]: [],
    [FIXTURE_TAB.LIVE]: [],
    [FIXTURE_TAB.PAST]: [],
  };
  for (const fx of fixtures) {
    out[bucketFixture(fx)].push(fx);
  }
  const asc = (a, b) => Number(a?.timestamp || 0) - Number(b?.timestamp || 0);
  const desc = (a, b) => Number(b?.timestamp || 0) - Number(a?.timestamp || 0);
  out[FIXTURE_TAB.NEED_ODDS].sort(asc);
  out[FIXTURE_TAB.UPCOMING].sort(asc);
  out[FIXTURE_TAB.LIVE].sort(asc);
  out[FIXTURE_TAB.PAST].sort(desc);
  return out;
};

export const formatFixtureKickoff = (item) => {
  const ko = getKickoffUnixSeconds(item);
  if (!ko) return "Schedule pending";
  return formatAppDateTime(ko * 1000);
};

export const fixtureMatchLabel = (item) => {
  const home = item?.homeTeam?.name || "Home";
  const away = item?.awayTeam?.name || "Away";
  return `${home} vs ${away}`;
};

export const resolveFixtureId = (item) =>
  String(item?._id || item?.id || item?.fixtureId || "");

export const fixtureStatusPayload = (item) => ({
  status: item?.status,
  date: item?.date || item?.fixture?.date,
  timestamp: item?.timestamp,
  goals: item?.goals,
});

export const fixtureKickoffShort = (item) => {
  const ko = getKickoffUnixSeconds(item);
  if (!ko) return "TBD";
  return formatAppDateTime(ko * 1000);
};

/** Kickoff within this window counts as "soon" (24h). */
export const KICKOFF_SOON_WINDOW_MS = 24 * 60 * 60 * 1000;

export const isKickoffSoon = (item, windowMs = KICKOFF_SOON_WINDOW_MS) => {
  const ko = getKickoffUnixSeconds(item);
  if (!Number.isFinite(ko)) return false;
  const diffMs = ko * 1000 - Date.now();
  return diffMs > 0 && diffMs <= windowMs;
};

/** Earliest future kickoff on the house board (excludes live & finished). */
export const pickNextKickoffFixture = (fixtures = []) => {
  const now = Math.floor(Date.now() / 1000);
  let best = null;
  let bestKo = Infinity;
  for (const fx of fixtures) {
    if (isTerminalFixture(fx) || isLiveFixture(fx)) continue;
    const ko = getKickoffUnixSeconds(fx);
    if (!Number.isFinite(ko) || ko <= now) continue;
    if (ko < bestKo) {
      bestKo = ko;
      best = fx;
    }
  }
  return best;
};

export const formatKickoffCountdown = (item, nowMs = Date.now()) => {
  const ko = getKickoffUnixSeconds(item);
  if (!Number.isFinite(ko)) return null;
  const diffMs = ko * 1000 - nowMs;
  if (diffMs <= 0) return { text: "Now", urgent: true };
  const totalSec = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  if (days > 0) {
    return { text: `${days}d ${hours}h`, urgent: false };
  }
  if (hours > 0) {
    return {
      text: `${hours}h ${String(mins).padStart(2, "0")}m`,
      urgent: hours < 2,
    };
  }
  return { text: `${mins}m`, urgent: true };
};

/** True when the API allows manual suspend (pre-kickoff, not live/finished). */
export const canSuspendFixture = (item) => {
  if (isTerminalFixture(item)) return false;
  if (isLiveFixture(item)) return false;
  const ko = getKickoffUnixSeconds(item);
  const now = Math.floor(Date.now() / 1000);
  if (ko != null && ko > 0 && ko <= now) return false;
  return true;
};

export const suspendDisabledReason = (item, { partnershipReady }) => {
  if (!partnershipReady) return "All partners must accept first";
  if (item.suspended) return null;
  if (!canSuspendFixture(item)) {
    return "Cannot suspend after kickoff or once the match is live";
  }
  return null;
};

export const removeDisabledReason = (item, { partnershipReady, bet, houseFixtureCount }) => {
  if (!partnershipReady) return "All partners must accept first";
  if (isTerminalFixture(item)) return "Past fixtures cannot be removed from the board";
  const minLegs = Number(bet?.minSelectionsPerBet ?? bet?.minFixture ?? 2);
  if (bet?.status === "active" && houseFixtureCount <= minLegs) {
    return `Active houses need at least ${minLegs} fixtures on the board`;
  }
  return null;
};

export const FIXTURE_ONE_X_TWO_CELLS = [
  { key: "1", label: "Home" },
  { key: "X", label: "Draw" },
  { key: "2", label: "Away" },
];

export const getFixtureOneXTwoFullTime = (item) =>
  item?.odds?.oneXTwo?.fullTime || null;

export const formatFixtureOneXTwoValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 1) return "—";
  return numeric.toFixed(2);
};

export const fixtureHasOneXTwoOdds = (item) => {
  const fullTime = getFixtureOneXTwoFullTime(item);
  if (!fullTime) return false;
  return FIXTURE_ONE_X_TWO_CELLS.some(({ key }) => {
    const numeric = Number(fullTime[key]);
    return Number.isFinite(numeric) && numeric > 1;
  });
};

